import { Component, computed, inject, input, output } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { ActionsHeaderComponent, LoaderService } from '@h5s/design-system';
import { FeatureDocService } from '@h5s/feature-doc';
import { IActionButton } from '@h5s/util-button';
import { filter, map, of, switchMap, take } from 'rxjs';
import { FeaturePsService } from '../../../feature-ps.service';
import { FEATURE_PS_TRANSLATIONS } from '../../translations/feature-ps.translations';
import { PsFilterModel } from '../ps-filter/models/ps-filters-model';

@Component({
  selector: 'h5s-ps-header',
  imports: [ActionsHeaderComponent],
  templateUrl: './ps-header.component.html',
  styleUrl: './ps-header.component.scss',
})
export class PsHeaderComponent {
  private readonly featurePsService = inject(FeaturePsService);
  private readonly loaderService = inject(LoaderService);
  private readonly featureDocService = inject(FeatureDocService);

  public readonly areGuestsSelected = computed(() => this.featurePsService.selectedTablePsCustomers().length > 0);

  /* Translations */
  public readonly translation = FEATURE_PS_TRANSLATIONS;

  public sendPS = output<void>();

  public readonly filters = input<PsFilterModel | null>(null);

  public readonly enableSendPs = toSignal(
    toObservable(this.filters).pipe(
      filter((filters): filters is PsFilterModel => !!filters),
      switchMap((filter) => {
        return this.featurePsService.getOwnerPublicSecurityById(filter.profile);
      }),
      map((op) => op.sendOnline),
    ),
  );

  public readonly enableDownloadReceipt = toSignal(
    toObservable(this.filters).pipe(
      filter((filters): filters is PsFilterModel => !!filters),
      switchMap((filter) => {
        return this.featurePsService.getOwnerPublicSecurityById(filter.profile);
      }),
      map((op) => op.sendOnline && op.publicSecurityType.receipt),
    ),
  );

  public readonly actions = computed<IActionButton[]>(() => {
    const actions: IActionButton[] = [
      {
        description: this.translation.buttonActions,
        buttonStyle: 'primarySecondary',
        suffixIcon: 'uil uil-angle-down',
        subactions: [
          {
            description: this.translation.buttonDownloadInvoice,
            icon: 'uil uil-cloud-download',
            tooltip: this.translation.tooltipDownloadInvoice,
            hidden: !this.enableSendPs(),
            action: (): void => {
              this.downloadPsReceipt();
            },
          },
          {
            description: this.translation.buttonSelectReady,
            action: (): void => {
              this.featurePsService.selectReadyPsCustomer.next();
            },
          },
        ],
      },
    ];

    const downloadPSAction: IActionButton = {
      description: this.translation.buttonDownloadPS,
      disabled: () => !this.areGuestsSelected(),
      action: (): void => {
        this.downloadFilePS();
      },
      buttonStyle: 'primary',
    };
    const sendPSAction: IActionButton = {
      buttonStyle: 'primary',
      description: this.translation.buttonSendPS,
      buttonId: 'sendPs',
      disabled: () => !this.areGuestsSelected(),
      action: (): void => {
        const filters = this.filters();
        if (!filters) {
          return;
        }

        this.sendPS.emit();
        const sendPs$ = this.featurePsService.sendPsCustomers(filters.date, filters.profile).pipe(take(1)).subscribe();

        this.loaderService.showLoadingButton({
          buttonId: 'sendPs',
          subscription: sendPs$,
          delay: 0,
        });
      },
      subactions: [
        {
          description: this.translation.buttonDownloadPS,
          icon: 'uil uil-cloud-download',
          action: (): void => {
            this.downloadFilePS();
          },
        },
      ],
    };

    actions.push(this.enableSendPs() ? sendPSAction : downloadPSAction);
    return actions;
  });

  public downloadFilePS(): void {
    const filters = this.filters();
    if (!filters) {
      return;
    }

    this.featurePsService
      .downloadPsFile(filters.date, filters.profile)
      .pipe(map((result) => this.featureDocService.openReport(() => of(result))))
      .subscribe({
        next: () => {
          this.featurePsService.research.next();
        },
        error: () => {
          this.featurePsService.research.next();
        },
      });
  }

  public downloadPsReceipt(): void {
    const filters = this.filters();
    if (!filters) {
      return;
    }

    this.featurePsService.openDownloadReceiptModal(filters.date, filters.profile);
  }
}
